import { useCallback, useEffect, useRef, useState } from 'react';
import { api, ErrorApi, fetchAutenticado } from '../api/cliente';
import type {
  AccionPago, Opcion, Orden, OrdenCompra, Pago, PagoDetalle, Pagina, Tot,
  ResumenEgresos,
} from '../api/tipos';
import { Cargando, Dialogo, DialogoMotivo, Error, Paginacion } from '../componentes/comunes';
import { useSesion } from '../sesion';
import { folioOT } from '../folio';
import { CotizacionesPorAutorizar, Ficha as FichaDeLaOrden } from './Ordenes';
import { FichaTot } from './Tot';
import { FichaOrden, Prioridad } from './Compras';
import Tablero from './Tablero';
import { aligerar } from '../imagenes';

/**
 * Las tres bandejas del circuito de egresos.
 *
 * Es la misma cadena vista desde tres sillas distintas, y por eso
 * comparten componentes:
 *
 *   Operaciones  valida el costo del técnico y el precio al cliente.
 *   Dirección    autoriza la salida de dinero.
 *   Contabilidad la ejecuta y guarda el comprobante.
 *
 * Que sean tres personas no es trámite: es lo que impide que alguien
 * invente un gasto, lo apruebe y lo cobre.
 */

function moneda(valor: number): string {
  return valor.toLocaleString('es-MX', { style: 'currency', currency: 'MXN' });
}

function fechaCorta(iso: string): string {
  return new Date(iso).toLocaleDateString('es-MX', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
}

const ETIQUETAS_PAGO: Record<string, string> = {
  pendiente_autorizacion: 'Por autorizar',
  autorizado: 'Autorizado, por pagar',
  pagado: 'Pagado',
  procedente_sin_pago: 'Cerrado sin pago',
  rechazado: 'Rechazado',
};

function useResumen(recarga: number) {
  const [resumen, setResumen] = useState<ResumenEgresos | null>(null);

  useEffect(() => {
    api.get<ResumenEgresos>('/pagos/resumen').then(setResumen).catch(() => undefined);
  }, [recarga]);

  return resumen;
}

function Tarjeta({ titulo, valor, alerta }: { titulo: string; valor: number; alerta?: boolean }) {
  return (
    <div className="tarjeta" style={{ cursor: 'default' }}>
      <div className="conteo">{titulo}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: alerta ? '#b02a37' : undefined }}>
        {valor}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════ Operaciones

export function Operaciones() {
  const { puede } = useSesion();
  // Sin tarjetas de resumen: las tres que había contaban costos por
  // validar y pagos por autorizar, y las dos primeras se fueron con la
  // sección. Los pagos tienen su propia bandeja en Contabilidad.
  const [seccion, setSeccion] = useState<'autorizaciones' | 'tot' | 'compras'>('autorizaciones');
  // Gerencia autoriza compras pero trabaja desde Operaciones, no desde
  // Dirección. La pestaña sigue al permiso y no al puesto: aparece para
  // quien pueda firmar la compra, esté donde esté.
  const firmaCompras = puede('compras', 'autorizar');
  // Los vales de taller externo también los firma Operaciones. La
  // pestaña va aquí y no en la pantalla de TOT porque aquí es donde
  // quien autoriza busca lo que le toca: tener una cuarta autorización en
  // otra sección obliga a acordarse de ir a mirarla.
  const firmaVales = puede('tot', 'autorizar');

  return (
    <>
      <div className="encabezado">
        <div>
          <h1>Operaciones</h1>
          <p className="sub">
            Lo que espera la firma de Operaciones: las cotizaciones que
            registra Soporte operativo, los vales de taller externo y las
            órdenes de compra.
          </p>
        </div>
      </div>

      <nav className="submenu">
        <button
          className={`enlace ${seccion === 'autorizaciones' ? 'activo' : ''}`}
          onClick={() => setSeccion('autorizaciones')}
        >
          Por autorizar
        </button>
        {firmaVales && (
          <button
            className={`enlace ${seccion === 'tot' ? 'activo' : ''}`}
            onClick={() => setSeccion('tot')}
          >
            TOT por autorizar
          </button>
        )}
        {firmaCompras && (
          <button
            className={`enlace ${seccion === 'compras' ? 'activo' : ''}`}
            onClick={() => setSeccion('compras')}
          >
            Órdenes de compra
          </button>
        )}
      </nav>

      {seccion === 'autorizaciones' && <CotizacionesPorAutorizar />}

      {seccion === 'tot' && <ValesPorAutorizar />}

      {seccion === 'compras' && <ComprasPorAutorizar conTitulo={false} />}

    </>
  );
}

/**
 * Los vales de taller externo que esperan la firma de Operaciones.
 *
 * Al autorizarlos nace la solicitud de pago para Contabilidad, ya con la
 * firma de quien autoriza. Se abre la misma ficha que usa la pantalla de
 * TOT: el vale que se decide aquí tiene que verse igual que en cualquier
 * otro lado, o serían dos versiones del mismo gasto.
 */
function ValesPorAutorizar() {
  const { puede } = useSesion();
  const [vales, setVales] = useState<Tot[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [abierto, setAbierto] = useState<Tot | null>(null);

  const cargar = useCallback(() => {
    setError(null);
    api.get<Pagina<Tot>>('/tot', { estatus: 'registrada', limite: 100 })
      .then((p) => setVales(p.data))
      .catch(setError);
  }, []);

  useEffect(cargar, [cargar]);

  return (
    <div className="panel">
      <Error error={error} />

      {!vales ? <Cargando /> : vales.length === 0 ? (
        <div className="vacio">No hay vales esperando tu autorización.</div>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Folio</th>
              <th>Orden</th>
              <th>Sucursal</th>
              <th>Taller</th>
              <th className="numero">Total</th>
              <th>Registrado</th>
            </tr>
          </thead>
          <tbody>
            {vales.map((v) => (
              <tr key={v.id}>
                <td className="mono">
                  <button className="enlace" onClick={() => setAbierto(v)}>{v.folio}</button>
                </td>
                <td className="mono">{v.orden ? folioOT(v.orden.folio) : '—'}</td>
                <td>
                  {v.orden?.sucursal
                    ? `${v.orden.sucursal.determinante} — ${v.orden.sucursal.nombre}`
                    : '—'}
                </td>
                <td>{v.proveedor?.nombre ?? '—'}</td>
                <td className="numero">{moneda(v.total)}</td>
                <td>{fechaCorta(v.fecha)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {abierto && (
        <FichaTot
          tot={abierto}
          puede={puede}
          onCerrar={() => { setAbierto(null); cargar(); }}
        />
      )}
    </div>
  );
}

/*
 * Aquí estaba «Costos por validar», y se quitó.
 *
 * Karin: «no entiendo para que existe esa seccion, yo creo que debemos
 * ocultarla, pero antes de eso.. revisa bien los flujos de uso».
 *
 * Se revisaron, y en QA: de las seis órdenes que esperaban validación,
 * las seis traían su cotización completa y ninguna descuadraba contra la
 * suma de sus renglones. La pantalla ya no capturaba nada —las cifras
 * las escribe la cotización y el cierre no deja pasar una correctiva con
 * costo sin ella— y lo único que hacía de más era generar un pago al
 * técnico que los vales TOT ya cubrían.
 *
 * Con ella se fueron el formulario de costo, el endpoint y la situación
 * «esperando costo» de Facturación, que preguntaba por esta firma y sin
 * nadie que la diera habría dejado a todas las correctivas atoradas.
 * Ver `facturacion/criterios.ts`.
 */

// ═════════════════════════════════════════════ Dirección y Contabilidad

/**
 * Dirección: todo lo que espera una firma, en un solo lugar.
 *
 * Autorizar una orden de compra y autorizar un pago son la misma decisión
 * —comprometer dinero de la empresa— y quien la toma no debería tener que
 * acordarse de entrar a Compras a buscar si hay algo pendiente. Lo que no
 * se ve en la bandeja propia, no se firma.
 */
export function Direccion() {
  // Abre en el tablero: quien entra a Dirección viene a ver cómo va la
  // empresa, y de ahí baja a firmar lo que haga falta. Al revés obliga a
  // buscar el resumen en una pestaña que no es la primera.
  const [seccion, setSeccion] = useState<'tablero' | 'pagos' | 'compras'>('tablero');

  return (
    <>
      <nav className="submenu">
        <button
          className={seccion === 'tablero' ? 'activo' : ''}
          onClick={() => setSeccion('tablero')}
        >
          Tablero
        </button>
        <button
          className={seccion === 'pagos' ? 'activo' : ''}
          onClick={() => setSeccion('pagos')}
        >
          Pagos
        </button>
        <button
          className={seccion === 'compras' ? 'activo' : ''}
          onClick={() => setSeccion('compras')}
        >
          Órdenes de compra
        </button>
      </nav>

      {seccion === 'tablero' && (
        <Tablero
          alVerCompras={() => setSeccion('compras')}
          alVerPagos={() => setSeccion('pagos')}
        />
      )}

      {seccion === 'pagos' && (
        <BandejaPagos
          titulo="Dirección"
          descripcion="Autoriza la salida de dinero. Contabilidad la ejecuta después."
          bandejaInicial="por_autorizar"
        />
      )}

      {seccion === 'compras' && <ComprasPorAutorizar />}
    </>
  );
}

/**
 * Las órdenes de compra esperando el visto bueno.
 *
 * Es la misma ficha de Compras: se abre el folio, se ven las partidas y
 * los totales, y se autoriza o se rechaza con motivo. Quien capturó la
 * orden no puede autorizarla, aunque tenga el permiso.
 */
function ComprasPorAutorizar({ conTitulo = true }: { conTitulo?: boolean }) {
  const [numero, setNumero] = useState(1);
  const [pagina, setPagina] = useState<Pagina<OrdenCompra> | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [abierta, setAbierta] = useState<string | null>(null);

  const cargar = useCallback(() => {
    setCargando(true);
    setError(null);
    api.get<Pagina<OrdenCompra>>('/ordenes-compra', {
      bandeja: 'por_autorizar', pagina: numero, limite: 25,
    })
      .then(setPagina)
      .catch(setError)
      .finally(() => setCargando(false));
  }, [numero]);

  useEffect(cargar, [cargar]);

  return (
    <>
      {conTitulo && (
        <div className="encabezado">
          <div>
            <h1>Órdenes de compra por autorizar</h1>
            <p className="sub">
              Aprobar la orden compromete el gasto: a partir de ahí ya no se
              edita y se le manda al proveedor tal cual.
            </p>
          </div>
        </div>
      )}

      <div className="panel">
        <Error error={error} />

        {cargando && !pagina ? <Cargando /> : pagina && (
          <>
            {pagina.data.length === 0 ? (
              <div className="vacio">No hay órdenes de compra esperando autorización.</div>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>Folio</th>
                    <th>Proveedor</th>
                    <th>Para la orden</th>
                    <th className="numero">Partidas</th>
                    <th className="numero">Total</th>
                    <th>Entrega estimada</th>
                  </tr>
                </thead>
                <tbody>
                  {pagina.data.map((o) => (
                    <tr key={o.id}>
                      <td className="mono">
                        <button className="enlace" onClick={() => setAbierta(o.id)}>
                          {folioOT(o.folio)}
                        </button>
                      </td>
                      <td>{o.proveedor?.nombre ?? '—'}</td>
                      <td className="mono">{o.ordenTrabajo ? folioOT(o.ordenTrabajo.folio) : '—'}</td>
                      <td className="numero">{o.numeroPartidas}</td>
                      <td className="numero">{moneda(o.total)}</td>
                      <td>
                        {o.fechaEntregaEstimada ? fechaCorta(o.fechaEntregaEstimada) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            <Paginacion
              pagina={pagina.pagina} limite={pagina.limite} total={pagina.total}
              onCambiar={setNumero}
            />
          </>
        )}
      </div>

      {abierta && (
        <FichaOrden
          id={abierta}
          onCerrar={() => setAbierta(null)}
          onCambio={cargar}
        />
      )}
    </>
  );
}

export function Contabilidad() {
  const [seccion, setSeccion] = useState<'porPagar' | 'historial'>('porPagar');

  return (
    <>
      <nav className="submenu">
        <button
          className={seccion === 'porPagar' ? 'activo' : ''}
          onClick={() => setSeccion('porPagar')}
        >
          Por pagar
        </button>
        <button
          className={seccion === 'historial' ? 'activo' : ''}
          onClick={() => setSeccion('historial')}
        >
          Pagos realizados
        </button>
      </nav>

      {seccion === 'porPagar' ? (
        <BandejaPagos
          titulo="Contabilidad"
          descripcion="Ejecuta los pagos y guarda el comprobante. Los que todavía no tienen firma también aparecen: pagarlos se puede, pero queda asentado."
          bandejaInicial="pendientes"
          conComprobantes
        />
      ) : (
        <Historial />
      )}
    </>
  );
}

/**
 * Lo que ya se pagó, con filtros y descarga.
 *
 * Un registro cronológico responde «¿qué salió esta semana?», y filtrado
 * por un proveedor se vuelve su estado de cuenta: cuándo se le pagó, con
 * qué referencia y por cuál orden de compra. Es la pregunta que llega de
 * fuera —«no nos ha caído lo de agosto»— y la que hoy se contestaba
 * revisando correos.
 *
 * El total es de todo lo filtrado y no de la página que se ve: un total
 * que cambia al pasar de página no cuadra con nada.
 */
function Historial() {
  const { puede } = useSesion();
  const [beneficiario, setBeneficiario] = useState('');
  const [proveedorId, setProveedorId] = useState('');
  // Igual que en la bandeja: el folio de la compra la abre entera.
  const [compraAbierta, setCompraAbierta] = useState<string | null>(null);
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [numero, setNumero] = useState(1);
  const [pagina, setPagina] = useState<PaginaPagos | null>(null);
  const [proveedores, setProveedores] = useState<Opcion[]>([]);
  const [cargando, setCargando] = useState(true);
  const [descargando, setDescargando] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [abierto, setAbierto] = useState<string | null>(null);

  const filtros = {
    bandeja: 'pagados',
    beneficiario: beneficiario || undefined,
    proveedorId: proveedorId || undefined,
    desde: desde || undefined,
    hasta: hasta || undefined,
  };

  useEffect(() => {
    api.get<Pagina<Opcion>>('/proveedores', { estado: 'activo', limite: 200 })
      .then((r) => setProveedores(r.data))
      .catch(() => undefined);
  }, []);

  const cargar = useCallback(() => {
    setCargando(true);
    setError(null);
    api.get<PaginaPagos>('/pagos', { ...filtros, pagina: numero, limite: 25 })
      .then(setPagina)
      .catch(setError)
      .finally(() => setCargando(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [beneficiario, proveedorId, desde, hasta, numero]);

  useEffect(cargar, [cargar]);

  async function descargar() {
    setDescargando(true);
    setError(null);
    try {
      await api.descargar('/pagos/exportar', filtros, 'pagos.xlsx');
    } catch (e) {
      setError(e);
    } finally {
      setDescargando(false);
    }
  }

  return (
    <>
      <div className="encabezado">
        <div>
          <h1>Pagos realizados</h1>
          <p className="sub">
            Filtra un proveedor y el listado se vuelve su estado de cuenta:
            qué se le pagó, cuándo y con qué referencia.
          </p>
        </div>
      </div>

      <div className="panel">
        <div className="controles">
          <select
            value={beneficiario}
            onChange={(e) => {
              setBeneficiario(e.target.value);
              if (e.target.value !== 'proveedor') setProveedorId('');
              setNumero(1);
            }}
          >
            <option value="">Todos los beneficiarios</option>
            <option value="proveedor">Sólo proveedores</option>
            <option value="tecnico">Sólo técnicos</option>
            <option value="otro">Sólo los demás</option>
          </select>

          {beneficiario !== 'tecnico' && (
            <select
              value={proveedorId}
              onChange={(e) => { setProveedorId(e.target.value); setNumero(1); }}
            >
              <option value="">Todos los proveedores</option>
              {proveedores.map((p) => <option key={p.id} value={p.id}>{p.nombre}</option>)}
            </select>
          )}

          <input
            type="date" value={desde} title="Desde"
            onChange={(e) => { setDesde(e.target.value); setNumero(1); }}
          />
          <input
            type="date" value={hasta} title="Hasta"
            onChange={(e) => { setHasta(e.target.value); setNumero(1); }}
          />

          {puede('contabilidad', 'exportar') && (
            <button onClick={descargar} disabled={descargando || !pagina?.total}>
              {descargando ? 'Generando…' : 'Descargar Excel'}
            </button>
          )}
        </div>

        <Error error={error} />

        {cargando && !pagina ? <Cargando /> : pagina && (
          <>
            <div className="aviso">
              <strong>{pagina.total}</strong>
              {pagina.total === 1 ? ' pago' : ' pagos'} en el filtro ·
              {' '}Neto pagado: <strong>{moneda(pagina.sumaNeto)}</strong>
            </div>

            {pagina.data.length === 0 ? (
              <div className="vacio">No hay pagos con ese filtro.</div>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Beneficiario</th>
                    <th>Origen</th>
                    <th>Concepto</th>
                    <th>OT</th>
                    <th className="numero">Neto</th>
                    <th>Referencia</th>
                  </tr>
                </thead>
                <tbody>
                  {pagina.data.map((p) => (
                    <tr key={p.id}>
                      <td>{p.fechaPago ? fechaCorta(p.fechaPago) : '—'}</td>
                      <td>
                        <button className="enlace" onClick={() => setAbierto(p.id)}>
                          {p.beneficiario ?? '—'}
                        </button>
                      </td>
                      {/* Origen es de dónde nace el pago; OT, a qué
                          trabajo pertenece. Antes iban en la misma
                          columna y ganaba la compra, así que el pago de
                          unas piezas no decía de qué orden eran.
                          El folio abre la compra entera. */}
                      <td className="mono">
                        {p.ordenCompra ? (
                          <button
                            className="enlace"
                            onClick={() => setCompraAbierta(p.ordenCompra!.id)}
                          >
                            {p.ordenCompra.folio}
                          </button>
                        ) : '—'}
                      </td>
                      <td style={{ maxWidth: 260 }}>
                        {p.ordenCompra?.concepto ?? p.concepto ?? '—'}
                      </td>
                      <td className="mono">
                        {p.ordenTrabajo ? folioOT(p.ordenTrabajo.folio) : ''}
                      </td>
                      <td className="numero">{moneda(p.montoNeto)}</td>
                      <td className="mono">{p.referenciaPago ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            <Paginacion
              pagina={pagina.pagina} limite={pagina.limite} total={pagina.total}
              onCambiar={setNumero}
            />
          </>
        )}
      </div>

      {compraAbierta && (
        <FichaOrden
          id={compraAbierta}
          onCerrar={() => setCompraAbierta(null)}
          onCambio={cargar}
        />
      )}

      {abierto && (
        <FichaPago
          id={abierto}
          conComprobantes
          onCerrar={() => setAbierto(null)}
          onCambio={cargar}
        />
      )}
    </>
  );
}

/** La lista de pagos trae además la suma de todo lo filtrado. */
interface PaginaPagos extends Pagina<Pago> {
  sumaMonto: number;
  sumaNeto: number;
}

function BandejaPagos({
  titulo, descripcion, bandejaInicial, conComprobantes,
}: {
  titulo: string;
  descripcion: string;
  bandejaInicial: string;
  conComprobantes?: boolean;
}) {
  const { puede } = useSesion();
  const [solicitando, setSolicitando] = useState(false);
  // La compra que se está mirando. El folio de la lista la abre: es la
  // misma ficha que usa Compras, con todo lo que capturó —zona, tienda,
  // cargo, partidas, retenciones— y no una tercera copia por mantener.
  const [compraAbierta, setCompraAbierta] = useState<string | null>(null);
  const [recarga, setRecarga] = useState(0);
  const resumen = useResumen(recarga);

  const [bandeja, setBandeja] = useState(bandejaInicial);
  const [beneficiario, setBeneficiario] = useState('');
  const [numero, setNumero] = useState(1);
  const [pagina, setPagina] = useState<Pagina<Pago> | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [abierto, setAbierto] = useState<string | null>(null);
  /** La orden de trabajo abierta desde su folio, sin salir de la bandeja. */
  const [ordenAbierta, setOrdenAbierta] = useState<string | null>(null);

  /**
   * Si esta bandeja junta pagos de varios estatus.
   *
   * «Sin autorizar» y «autorizados, por pagar» son un solo estatus cada
   * una, así que ahí la columna repetiría el filtro. Las otras tres
   * mezclan, y ahí sí hace falta.
   */
  const laBandejaMezclaEstatus = bandeja !== 'por_autorizar' && bandeja !== 'por_pagar';

  const cargar = useCallback(() => {
    setCargando(true);
    setError(null);
    api.get<Pagina<Pago>>('/pagos', {
      bandeja: bandeja || undefined,
      beneficiario: beneficiario || undefined,
      pagina: numero,
      limite: 25,
    })
      .then(setPagina)
      .catch(setError)
      .finally(() => setCargando(false));
  }, [bandeja, beneficiario, numero, recarga]);

  useEffect(cargar, [cargar]);

  return (
    <>
      <div className="encabezado">
        <div>
          <h1>{titulo}</h1>
          <p className="sub">{descripcion}</p>
        </div>
      </div>

      {resumen && (
        <div className="rejilla-catalogos">
          <Tarjeta
            titulo="Pagos a proveedor por autorizar"
            valor={resumen.porAutorizarProveedor}
            alerta={bandejaInicial === 'por_autorizar' && resumen.porAutorizarProveedor > 0}
          />
          <Tarjeta
            titulo="Pagos a técnico por autorizar"
            valor={resumen.porAutorizarTecnico}
            alerta={bandejaInicial === 'por_autorizar' && resumen.porAutorizarTecnico > 0}
          />
          <Tarjeta
            titulo="Por pagar"
            valor={resumen.porPagar}
            alerta={bandejaInicial === 'pendientes' && resumen.porPagar > 0}
          />
        </div>
      )}

      <div className="panel">
        <div className="controles">
          <select value={bandeja} onChange={(e) => { setBandeja(e.target.value); setNumero(1); }}>
            <option value="pendientes">Todo lo que falta pagar</option>
            <option value="por_autorizar">Sin autorizar</option>
            <option value="por_pagar">Autorizados, por pagar</option>
            <option value="cerrados">Cerrados</option>
            <option value="">Todos</option>
          </select>
          <select
            value={beneficiario}
            onChange={(e) => { setBeneficiario(e.target.value); setNumero(1); }}
          >
            <option value="">Todos los beneficiarios</option>
            <option value="proveedor">Sólo proveedores</option>
            <option value="tecnico">Sólo técnicos</option>
            <option value="otro">Sólo los demás</option>
          </select>

          {/* Un anticipo, unos viáticos, la herramienta que tuvo que
              comprar: pagos que existen antes de que la orden cierre y que
              antes salían del sistema. */}
          {(puede('operaciones', 'crear') || puede('contabilidad', 'crear')) && (
            <button className="primario" onClick={() => setSolicitando(true)}>
              Solicitar pago
            </button>
          )}
        </div>

        <Error error={error} />

        {cargando && !pagina ? <Cargando /> : pagina && (
          <>
            {pagina.data.length === 0 ? (
              <div className="vacio">No hay pagos en esta bandeja.</div>
            ) : (
              <table>
                <thead>
                  <tr>
                    {/* De dónde viene el gasto: la orden de trabajo, y
                        debajo la compra cuando el pago nace de una. Antes
                        era una columna que enseñaba una o la otra, y
                        nunca las dos. */}
                    <th>Origen</th>
                    <th>Prior.</th>
                    <th>Beneficiario</th>
                    {/* Qué se está pagando. Beneficiario y monto no
                        alcanzan para saberlo, y obligaban a abrir orden
                        por orden. */}
                    <th>Concepto</th>
                    {/* La sucursal con su zona debajo. La zona sola casi
                        nunca se busca: se busca para ubicar la tienda. */}
                    <th>Sucursal</th>
                    {/* El neto, que es lo que de verdad se transfiere, y
                        el bruto debajo sólo cuando hay retención. Eran dos
                        columnas y el riesgo era transferir la otra. */}
                    <th className="numero">A pagar</th>
                    <th>¿Genera costo?</th>
                    {/* La cuenta regresiva. Karin: «como en cuenta
                        regresiva para que sepa cuando pagarlo». Va antes
                        de la fecha de captura porque es la que decide
                        qué se paga hoy; la otra sólo dice desde cuándo
                        está ahí. */}
                    <th>Vence</th>
                    <th>Fecha</th>
                  </tr>
                </thead>
                <tbody>
                  {pagina.data.map((p) => (
                    <tr key={p.id}>
                      <td className="mono">
                        {/* Las dos, cuando las hay: la OT dice de qué
                            trabajo es el gasto y la OC qué se compró.
                            Cada folio abre su ficha. */}
                        <div>
                          {p.ordenTrabajo ? (
                            <button
                              className="enlace"
                              onClick={() => setOrdenAbierta(p.ordenTrabajo!.id)}
                            >
                              {folioOT(p.ordenTrabajo.folio)}
                            </button>
                          ) : <span className="ayuda">—</span>}
                        </div>
                        {p.ordenCompra && (
                          <div>
                            <button
                              className="enlace ayuda"
                              onClick={() => setCompraAbierta(p.ordenCompra!.id)}
                            >
                              {p.ordenCompra.folio}
                            </button>
                          </div>
                        )}
                      </td>
                      {/* La que puso Compras. En un pago que no nace de
                          una orden de compra no hay ninguna. */}
                      <td>
                        {p.ordenCompra?.prioridad
                          ? <Prioridad valor={p.ordenCompra.prioridad} />
                          : '—'}
                      </td>
                      <td>
                        <button className="enlace" onClick={() => setAbierto(p.id)}>
                          {p.beneficiario ?? '—'}
                        </button>
                      </td>
                      <td style={{ maxWidth: 220 }}>
                        <div>{p.ordenCompra?.concepto ?? p.concepto ?? '—'}</div>
                        {/* Cuántas piezas. Una banda y treinta bandas se
                            leían igual, y es la diferencia entre revisar
                            el importe y darlo por bueno. */}
                        {p.ordenCompra?.piezas ? (
                          <div className="ayuda">
                            {p.ordenCompra.piezas === 1
                              ? '1 pieza'
                              : `${p.ordenCompra.piezas} piezas`}
                          </div>
                        ) : null}
                      </td>
                      <td>
                        {p.ordenTrabajo?.sucursal ? (
                          <>
                            <div>
                              {p.ordenTrabajo.sucursal.determinante}
                              {' — '}
                              {p.ordenTrabajo.sucursal.nombre}
                            </div>
                            {p.ordenTrabajo.region && (
                              <div className="ayuda">{p.ordenTrabajo.region.nombre}</div>
                            )}
                          </>
                        ) : '—'}
                      </td>
                      <td className="numero">
                        <div>{moneda(p.montoNeto)}</div>
                        {/* El bruto sólo cuando difiere. Repetir la misma
                            cifra dos veces enseña a no leer la segunda. */}
                        {p.retencion > 0 && (
                          <div className="ayuda">de {moneda(p.monto)}</div>
                        )}
                      </td>
                      {/* Guion y no «No» cuando el pago no cuelga de una
                          orden: una compra de abasto no tiene esa
                          pregunta, y un «No» diría que alguien la
                          contestó. */}
                      <td>
                        {!p.ordenTrabajo ? '—'
                          : p.ordenTrabajo.generaCosto === null ? (
                            <span className="ayuda">Sin decidir</span>
                          ) : (
                            <span className={`etiqueta ${p.ordenTrabajo.generaCosto ? 'si' : ''}`}>
                              {p.ordenTrabajo.generaCosto ? 'Sí' : 'No'}
                            </span>
                          )}
                      </td>
                      <td>
                        {/*
                          Sin plazo se pinta un guion y no la frase.

                          «Sin plazo pactado» en cada renglón de una
                          bandeja donde la mayoría no lo tiene es ruido
                          que tapa a los tres que sí vencen. La frase
                          completa vive en la ficha del pago.
                        */}
                        {p.plazo.urgencia === 'sin_plazo'
                          ? <span className="ayuda">—</span>
                          : (
                            <>
                              <span className={`etiqueta ${
                                p.plazo.urgencia === 'vencido' || p.plazo.urgencia === 'hoy'
                                  ? 'no'
                                  : p.plazo.urgencia === 'proximo' ? '' : 'si'
                              }`}
                              >
                                {p.plazo.texto}
                              </span>
                              {p.venceEl && (
                                <div className="ayuda">{fechaCorta(p.venceEl)}</div>
                              )}
                            </>
                          )}
                      </td>
                      <td>
                        <div>{fechaCorta(p.creadoEn)}</div>
                        {/* El estatus va aquí y sólo donde hace falta: en
                            «sin autorizar» y en «por pagar» la bandeja ya
                            es el estatus, y una columna que repite el
                            filtro es una columna que nadie lee. */}
                        {laBandejaMezclaEstatus && (
                          <div>
                            <span className={`etiqueta ${p.estatus === 'pagado' ? 'si' : p.estatus === 'rechazado' ? 'no' : ''}`}>
                              {ETIQUETAS_PAGO[p.estatus] ?? p.estatus}
                            </span>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            <Paginacion
              pagina={pagina.pagina} limite={pagina.limite} total={pagina.total}
              onCambiar={setNumero}
            />
          </>
        )}
      </div>

      {solicitando && (
        <SolicitarPago
          onCerrar={() => setSolicitando(false)}
          onCreado={() => { setSolicitando(false); cargar(); setRecarga((n) => n + 1); }}
        />
      )}

      {compraAbierta && (
        <FichaOrden
          id={compraAbierta}
          onCerrar={() => setCompraAbierta(null)}
          onCambio={cargar}
        />
      )}

      {/* La orden de trabajo, desde su folio. Contabilidad no reparte
          técnicos ni tiene el permiso, así que la lista va vacía: la
          ficha se abre para leerla, no para asignar. */}
      {ordenAbierta && (
        <FichaDeLaOrden
          id={ordenAbierta}
          tecnicos={[]}
          onCerrar={() => setOrdenAbierta(null)}
          onCambio={cargar}
        />
      )}

      {abierto && (
        <FichaPago
          id={abierto}
          conComprobantes={Boolean(conComprobantes)}
          onCerrar={() => setAbierto(null)}
          onCambio={() => { cargar(); setRecarga((n) => n + 1); }}
        />
      )}
    </>
  );
}

function FichaPago({
  id, conComprobantes, onCerrar, onCambio,
}: {
  id: string; conComprobantes: boolean; onCerrar: () => void; onCambio: () => void;
}) {
  const [pago, setPago] = useState<PagoDetalle | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [accion, setAccion] = useState<AccionPago | null>(null);
  const [trabajando, setTrabajando] = useState(false);
  const archivoRef = useRef<HTMLInputElement>(null);
  /** La orden y la compra, abiertas desde sus folios sin salir del pago. */
  const [ordenAbierta, setOrdenAbierta] = useState<string | null>(null);
  const [compraAbierta, setCompraAbierta] = useState<string | null>(null);
  const [abonando, setAbonando] = useState(false);
  /** Rehacer el reparto del pago, mientras no haya salido dinero. */
  const [repartiendo, setRepartiendo] = useState(false);
  const { puede } = useSesion();

  const cargar = useCallback(() => {
    api.get<PagoDetalle>(`/pagos/${id}`).then(setPago).catch(setError);
  }, [id]);

  useEffect(cargar, [cargar]);

  async function subir(archivo: File) {
    setError(null);
    setTrabajando(true);
    try {
      const cuerpo = new FormData();
      cuerpo.append('archivo', await aligerar(archivo));

      const respuesta = await fetchAutenticado(`/api/v1/pagos/${id}/comprobantes`, {
        method: 'POST',
        body: cuerpo,
      });

      if (!respuesta.ok) {
        const cuerpoError = await respuesta.json().catch(() => ({}));
        throw new ErrorApi(
          respuesta.status,
          String(cuerpoError.codigo_negocio ?? 'error'),
          String(cuerpoError.detail ?? 'No se pudo subir el comprobante.'),
        );
      }
      cargar();
    } catch (e) {
      setError(e);
    } finally {
      setTrabajando(false);
      if (archivoRef.current) archivoRef.current.value = '';
    }
  }

  /**
   * Si este pago admite abonos.
   *
   * Los mismos estados que acepta la API —ni uno más, para que el botón
   * que existe sea un botón que funciona— y sólo mientras quede saldo.
   */
  const puedeAbonar = Boolean(
    pago
    && !pago.saldo.saldado
    && (pago.estatus === 'autorizado' || pago.estatus === 'pendiente_autorizacion')
    && puede('contabilidad', 'editar'),
  );

  /**
   * Si todavía se puede rehacer el plan de pago, y si a quien mira le toca.
   *
   * Karin: «gerencia al momento de autorizar y previo a que le llegue a
   * contabilidad, es quien decide si se paga parcial o total». La decisión
   * se toma al autorizar, pero ahí se acaba el formulario: un pago que se
   * autorizó completo —o uno nacido antes de que el plan existiera— sólo
   * se podía partir rechazándolo y volviéndolo a autorizar, que deja dos
   * firmas y un motivo de rechazo que no es verdad.
   *
   * El límite es el primer abono: con dinero fuera, el plan ya se está
   * ejecutando.
   */
  const puedeRepartir = Boolean(
    pago
    && !pago.saldo.saldado
    && pago.saldo.abonado === 0
    && (pago.estatus === 'autorizado' || pago.estatus === 'pendiente_autorizacion')
    && (puede('direccion', 'autorizar') || puede('operaciones', 'autorizar')),
  );

  if (!pago) {
    return (
      <Dialogo titulo="Pago" onCerrar={onCerrar} pie={<button onClick={onCerrar}>Cerrar</button>}>
        <Error error={error} />
        {!error && <Cargando />}
      </Dialogo>
    );
  }

  return (
    <>
      <Dialogo
        titulo={`${pago.beneficiario ?? 'Pago'} · ${ETIQUETAS_PAGO[pago.estatus] ?? pago.estatus}`}
        onCerrar={onCerrar}
        pie={<button onClick={onCerrar}>Cerrar</button>}
      >
        <Error error={error} />

        {/* Se admite y se dice. Mientras sea una sola persona la que
            autoriza y paga, lo que queda del control no es impedirlo sino
            que se vea. */}
        {pago.autorizoYPago && (
          <div className="aviso">
            Lo autorizó y lo pagó la misma persona. Queda asentado como excepción
            en la bitácora.
          </div>
        )}

        {pago.motivoRechazo && <div className="aviso error">Rechazado: {pago.motivoRechazo}</div>}
        {pago.motivoSinPago && <div className="aviso">Sin pago: {pago.motivoSinPago}</div>}

        {/*
          Todo lo que se sabe del pago, junto.

          Karin: «en la interna agrega todo lo que tenemos, entre más
          completa mejor». Aquí hay espacio, a diferencia de la bandeja:
          quien abre esta ficha está a punto de firmar o de transferir, y
          cada dato que no está aquí es una pestaña más que abrir.
        */}
        <div className="rejilla-datos">
          <Dato etiqueta="Beneficiario" valor={pago.beneficiario} />
          <Dato
            etiqueta="Tipo de beneficiario"
            valor={pago.beneficiarioTipo === 'proveedor' ? 'Proveedor'
              : pago.beneficiarioTipo === 'tecnico' ? 'Técnico' : 'Otro'}
          />
          <Dato etiqueta="Concepto" valor={pago.concepto ?? pago.ordenCompra?.concepto ?? null} />
          <Dato
            etiqueta="Estatus"
            valor={ETIQUETAS_PAGO[pago.estatus] ?? pago.estatus}
          />
          {/* El folio abre la orden entera, sin salir del pago. Antes era
              texto y para ver de qué trabajo se trataba había que irse a
              buscarlo a otra pantalla. */}
          <Dato
            etiqueta="Orden de trabajo"
            valor={pago.ordenTrabajo ? folioOT(pago.ordenTrabajo.folio) : null}
            alClicar={pago.ordenTrabajo
              ? () => setOrdenAbierta(pago.ordenTrabajo!.id)
              : undefined}
          />
          <Dato
            etiqueta="Orden de compra"
            valor={pago.ordenCompra?.folio ?? null}
            alClicar={pago.ordenCompra
              ? () => setCompraAbierta(pago.ordenCompra!.id)
              : undefined}
          />
          <Dato
            etiqueta="Sucursal"
            valor={pago.ordenTrabajo?.sucursal
              ? `${pago.ordenTrabajo.sucursal.determinante} — ${pago.ordenTrabajo.sucursal.nombre}`
              : null}
          />
          <Dato etiqueta="Zona" valor={pago.ordenTrabajo?.region?.nombre ?? null} />
          {/* La decisión que dice si ese dinero vuelve. Un pago sin orden
              detrás no tiene esta pregunta, y por eso puede venir vacío en
              vez de decir «No». */}
          <Dato
            etiqueta="¿Genera costo al cliente?"
            valor={!pago.ordenTrabajo ? null
              : pago.ordenTrabajo.generaCosto === null ? 'Sin decidir'
                : pago.ordenTrabajo.generaCosto
                  ? 'Sí, se le cobra aparte de la iguala'
                  : 'No, lo absorbe la iguala'}
          />
          <Dato
            etiqueta="Prioridad de la compra"
            valor={pago.ordenCompra?.prioridad
              ? ETIQUETAS_PRIORIDAD[pago.ordenCompra.prioridad] ?? pago.ordenCompra.prioridad
              : null}
          />
          <Dato
            etiqueta="De dónde nació"
            valor={pago.origen === 'validacion_costo'
              ? 'De validar el costo de la orden'
              : 'Capturado a mano'}
          />
          <Dato etiqueta="Solicitado" valor={fechaCorta(pago.creadoEn)} />
          {/*
            Aquí sí con la frase completa, y con la fecha debajo.

            En la lista va en corto porque son veinte renglones; aquí es
            uno solo y lo que hace falta es saber hasta cuándo, no sólo
            cuánto falta.
          */}
          <Dato
            etiqueta="Vence"
            valor={pago.venceEl
              ? `${pago.plazo.texto} · ${fechaCorta(pago.venceEl)}`
              : null}
            detalle={pago.venceEl
              ? 'El plazo se cuenta desde la fecha de la factura del proveedor,'
                + ' no desde que se capturó.'
              : 'No se pactó plazo con el proveedor: se paga cuando'
                + ' Contabilidad lo ejecute.'}
          />
        </div>

        {/*
          Lo que no cuadra entre este pago y su compra, antes que el
          importe.

          Va arriba y en rojo porque es lo único que importa si aparece:
          quien está a punto de transferir tiene que verlo antes que el
          número, no debajo. Casi siempre no hay nada.
        */}
        {pago.descuadres.length > 0 && (
          <div className="aviso error" style={{ marginBottom: 12 }}>
            <strong>Este pago no cuadra con su orden de compra.</strong>
            <ul style={{ margin: '6px 0 0 18px' }}>
              {pago.descuadres.map((d, i) => <li key={i}>{d.mensaje}</li>)}
            </ul>
          </div>
        )}

        <div className="aviso">
          <div style={{ display: 'grid', gap: 4 }}>
            {/*
              El desglose de la compra, no una copia guardada en el pago.

              El pago sólo guarda el neto —el total de la orden ya viene
              con la retención restada— así que sin esto contabilidad veía
              «monto 5,300 · neto 5,300» y no tenía con qué cuadrar contra
              una factura que trae subtotal, IVA y retención por separado.
              Peor: parecía que no hubo retención cuando sí la hubo.
            */}
            {pago.ordenCompra?.desglose && (
              <>
                <span className="ayuda">
                  Según la orden de compra {pago.ordenCompra.folio}
                </span>
                <span>Subtotal: {moneda(pago.ordenCompra.desglose.subtotal)}</span>
                <span>IVA: {moneda(pago.ordenCompra.desglose.iva)}</span>
                {pago.ordenCompra.desglose.retencion > 0 && (
                  <span>
                    Retención: <strong>−{moneda(pago.ordenCompra.desglose.retencion)}</strong>
                    <span className="ayuda"> · no se transfiere, se declara</span>
                  </span>
                )}
                <hr style={{ border: 0, borderTop: '1px solid var(--border)', margin: '4px 0' }} />
              </>
            )}

            <span>Monto: <strong>{moneda(pago.monto)}</strong></span>
            {pago.retencion > 0 && (
              <span>Retención: <strong>−{moneda(pago.retencion)}</strong></span>
            )}
            <span style={{ fontSize: 16 }}>
              Neto a pagar: <strong>{moneda(pago.montoNeto)}</strong>
            </span>
            {/* Y lo que falta, cuando se abonó en partes.
                Karin, de APRISA: «hay ocasiones que piden un anticipo
                antes de hacer el trabajo y el resto contra entrega». Un
                pago a medias no es «por pagar» ni «pagado»: la primera
                esconde que ya salió dinero y la segunda que todavía se
                debe, y las dos engañan a quien decide si la orden puede
                cerrar. */}
            {pago.abonos.length > 0 && !pago.saldo.saldado && (
              <>
                <span>Abonado: <strong>{moneda(pago.saldo.abonado)}</strong></span>
                <span style={{ fontSize: 16 }}>
                  Falta: <strong>{moneda(pago.saldo.falta)}</strong>
                </span>
              </>
            )}

            {/* El plan que autorizó gerencia, junto al importe y no en
                otra sección.
                Karin, probando el TOT 16: «autorice el gasto desde
                gerencia y le puse anticipo de 500 y a contabilidad le
                sale completo». El anticipo estaba guardado, pero aquí
                sólo se leía «Neto a pagar: $1,044.00», así que quien iba
                a transferir no tenía manera de enterarse. Un dato que
                cambia cuánto sale del banco no puede estar escondido. */}
            {pago.saldo.anticipoAutorizado !== null && !pago.saldo.saldado && (
              <span style={{ fontSize: 16 }}>
                Anticipo autorizado:{' '}
                <strong>{moneda(pago.saldo.anticipoAutorizado)}</strong>
                {' · '}liquidación{' '}
                <strong>
                  {moneda(pago.montoNeto - pago.saldo.anticipoAutorizado)}
                </strong>
              </span>
            )}
          </div>
        </div>

        {/* Y por qué no aparece «Registrar pago».
            Sin esto, el botón simplemente no está y la pantalla parece
            rota en vez de parecer distinta: es el mismo defecto del
            desplegable vacío que no decía por qué estaba vacío. */}
        {pago.porQueNoSePagaCompleto && (
          <div className="aviso">{pago.porQueNoSePagaCompleto}</div>
        )}

        {/* Cada salida de dinero, con su fecha y su referencia.
            Sólo cuando hay más de una: con un solo abono, la fecha y la
            referencia ya están arriba y repetirlas no dice nada. */}
        {pago.abonos.length > 1 && (
          <>
            <h2>Abonos</h2>
            <table>
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th className="numero">Monto</th>
                  <th>Referencia</th>
                  <th>Concepto</th>
                </tr>
              </thead>
              <tbody>
                {pago.abonos.map((a) => (
                  <tr key={a.id}>
                    <td>{fechaCorta(a.fechaPago)}</td>
                    <td className="numero">{moneda(a.monto)}</td>
                    <td className="mono">{a.referencia}</td>
                    <td>{a.concepto ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        {/* Qué se compró, renglón por renglón.
            El concepto de la lista junta los nombres en una línea y
            pierde cantidades e importes, que es justo lo que se compara
            contra la factura del proveedor antes de transferir. */}
        {pago.ordenCompra?.partidas && pago.ordenCompra.partidas.length > 0 && (
          <>
            <h2>Lo que se compró</h2>
            <table>
              <thead>
                <tr>
                  <th>Descripción</th>
                  <th className="numero">Cant.</th>
                  <th className="numero">Costo unitario</th>
                </tr>
              </thead>
              <tbody>
                {pago.ordenCompra.partidas.map((r, i) => (
                  <tr key={i}>
                    <td>{r.descripcion}</td>
                    <td className="numero">{r.cantidad}</td>
                    <td className="numero">
                      {r.montoCompra === null ? '—' : moneda(r.montoCompra)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        {/* Y lo que dice la cotización de la orden.
            Karin: «me falta ver la descripción, lo que dice en la
            cotización». Vivía en otra pantalla: había que abrir la orden,
            buscar la sección y volver. */}
        {pago.cotizacion.length > 0 && (
          <>
            <h2>Lo que dice la cotización de la orden</h2>
            <table>
              <thead>
                <tr>
                  <th>Descripción</th>
                  <th>Tipo</th>
                  <th className="numero">Cant.</th>
                  <th className="numero">Costo</th>
                  <th className="numero">Venta</th>
                </tr>
              </thead>
              <tbody>
                {pago.cotizacion.map((r) => (
                  <tr key={r.id}>
                    <td>{r.descripcion}</td>
                    <td>{r.tipo === 'servicio' ? 'Servicio' : 'Refacción'}</td>
                    <td className="numero">{r.cantidad}</td>
                    {/* Nulo es «sin capturar» y cero es una decisión: se
                        revisó y no se cobra. Enseñar cero en los dos
                        borraría la diferencia justo donde se mira dinero. */}
                    <td className="numero">
                      {r.precioCosto === null
                        ? <span className="ayuda">Sin capturar</span>
                        : moneda(r.precioCosto)}
                    </td>
                    <td className="numero">
                      {r.precioVenta === null
                        ? <span className="ayuda">Sin capturar</span>
                        : moneda(r.precioVenta)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        {/* A qué cuenta se deposita, junto al monto. Mandar a quien paga
            al catálogo de proveedores a buscar la CLABE es cómo se
            transfiere a la cuenta equivocada: se copia de otra pestaña o
            de la memoria. */}
        {pago.cuenta && (
          <>
            <h2>Cuenta para el depósito</h2>
            {!pago.cuenta.tieneDatos ? (
              <div className="aviso error">
                Este {pago.beneficiarioTipo === 'proveedor' ? 'proveedor' : 'técnico'} no
                tiene datos bancarios capturados. Se dan de alta en su ficha del catálogo.
              </div>
            ) : (
              <>
                <Dato etiqueta="Banco" valor={pago.cuenta.banco} />
                <Dato etiqueta="Titular" valor={pago.cuenta.titular} />
                {pago.cuenta.rfc && <Dato etiqueta="RFC" valor={pago.cuenta.rfc} />}
                <div className="campo">
                  <label>CLABE</label>
                  <span className="mono">{pago.cuenta.clabe ?? '—'}</span>
                </div>
                <div className="campo">
                  <label>Número de cuenta</label>
                  <span className="mono">{pago.cuenta.numeroCuenta ?? '—'}</span>
                </div>
                {!pago.cuenta.enClaro && (
                  <span className="ayuda">
                    Se muestran enmascaradas: hace falta el permiso de datos bancarios
                    para verlas completas.
                  </span>
                )}
              </>
            )}
          </>
        )}

        {pago.autorizadoEn && (
          <Dato etiqueta="Autorizado" valor={fechaCorta(pago.autorizadoEn)} />
        )}
        {pago.pagadoEn && (
          <>
            <Dato etiqueta="Pagado" valor={fechaCorta(pago.pagadoEn)} />
            <Dato etiqueta="Referencia" valor={pago.referenciaPago} />
          </>
        )}

        {(pago.acciones.length > 0 || puedeAbonar || puedeRepartir) && (
          <div className="controles">
            {pago.acciones.map((a) => (
              <button
                key={a.a}
                className={a.a === 'rechazado' ? 'peligro' : 'primario'}
                onClick={() => setAccion(a)}
                title={a.explicacion}
              >
                {a.etiqueta}
              </button>
            ))}
            {/* El anticipo. «Registrar pago» sigue siendo pagar todo lo
                que falta de una vez, que es el caso normal; esto es el
                mismo movimiento en partes. */}
            {puedeAbonar && (
              <button
                onClick={() => setAbonando(true)}
                title="Para cuando se pactó anticipo y liquidación: registra lo que salió ahora y deja el resto pendiente."
              >
                Registrar un abono
              </button>
            )}
            {/* Rehacer el reparto sin rechazar el pago. Es de quien
                autoriza, no de quien paga, y por eso pregunta por otro
                permiso que el botón de al lado. */}
            {puedeRepartir && (
              <button
                onClick={() => setRepartiendo(true)}
                title="Decide si este pago se ejecuta completo o con anticipo. Mientras no haya salido dinero, se puede rehacer."
              >
                {pago.saldo.anticipoAutorizado
                  ? 'Cambiar el plan de pago'
                  : 'Autorizar un anticipo'}
              </button>
            )}
          </div>
        )}

        {conComprobantes && (
          <>
            <h2>Comprobantes</h2>
            {pago.comprobantes.length === 0 ? (
              <p className="sub">Sin comprobante todavía.</p>
            ) : (
              <ul style={{ listStyle: 'none', display: 'grid', gap: 6 }}>
                {pago.comprobantes.map((c) => (
                  <li key={c.id}>
                    {/* No es un enlace: el token vive sólo en memoria y no
                        viaja en una navegación, así que abrirlo así daba
                        401 y el comprobante parecía perdido. */}
                    <button
                      className="enlace mono"
                      onClick={() => {
                        void api.descargar(`/pagos/${id}/comprobantes/${c.id}`, undefined, c.nombre)
                          .catch(setError);
                      }}
                    >
                      {c.nombre}
                    </button>
                  </li>
                ))}
              </ul>
            )}

            <div className="campo">
              <label htmlFor="comprobante">Subir comprobante</label>
              <input
                id="comprobante"
                ref={archivoRef}
                type="file"
                accept="application/pdf,image/jpeg,image/png"
                disabled={trabajando}
                onChange={(e) => {
                  const archivo = e.target.files?.[0];
                  if (archivo) void subir(archivo);
                }}
              />
              <span className="ayuda">
                PDF o imagen, hasta 10 MB. Se conserva cinco años por
                retención fiscal, así que no hay forma de borrarlo.
              </span>
            </div>
          </>
        )}
      </Dialogo>

      {accion && (
        <EjecutarPago
          pagoId={id}
          accion={accion}
          neto={pago?.montoNeto ?? 0}
          onCerrar={() => setAccion(null)}
          onHecho={() => { setAccion(null); cargar(); onCambio(); }}
        />
      )}

      {repartiendo && pago && (
        <PlanDePago
          pagoId={id}
          neto={pago.montoNeto}
          anticipoActual={pago.saldo.anticipoAutorizado}
          onCerrar={() => setRepartiendo(false)}
          onHecho={() => { setRepartiendo(false); cargar(); onCambio(); }}
        />
      )}

      {abonando && pago && (
        <RegistrarAbono
          pagoId={id}
          falta={pago.saldo.falta}
          toca={pago.saldo.tocaAbonar}
          esElAnticipo={pago.saldo.esElAnticipo}
          onCerrar={() => setAbonando(false)}
          onHecho={() => { setAbonando(false); cargar(); onCambio(); }}
        />
      )}

      {/* La orden y la compra, encima del pago. Al cerrarlas se vuelve
          aquí, que es donde estaba la decisión a medias. */}
      {ordenAbierta && (
        <FichaDeLaOrden
          id={ordenAbierta}
          tecnicos={[]}
          onCerrar={() => setOrdenAbierta(null)}
          onCambio={cargar}
        />
      )}

      {compraAbierta && (
        <FichaOrden
          id={compraAbierta}
          onCerrar={() => setCompraAbierta(null)}
          onCambio={cargar}
        />
      )}
    </>
  );
}

function EjecutarPago({
  pagoId, accion, neto, onCerrar, onHecho,
}: {
  pagoId: string;
  accion: AccionPago;
  /** Lo que se debe. Hace falta para repartirlo al autorizar. */
  neto: number;
  onCerrar: () => void;
  onHecho: () => void;
}) {
  const [motivo, setMotivo] = useState('');
  const [referencia, setReferencia] = useState('');
  const [fechaPago, setFechaPago] = useState('');
  const [anticipo, setAnticipo] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [trabajando, setTrabajando] = useState(false);

  const pideMotivo = accion.requiere?.includes('motivo') ?? false;
  const pideReferencia = accion.requiere?.includes('referencia') ?? false;
  /**
   * Autorizar es el momento —el único— de decidir si se paga en partes.
   *
   * Karin: «gerencia al momento de autorizar y previo a que le llegue a
   * contabilidad, es quien decide si se paga parcial o total». Faltaba
   * aquí: la pregunta estaba en el vale pero no en el pago suelto, así
   * que quien autorizaba por esta pantalla no tenía dónde decirlo y el
   * pago salía siempre completo.
   */
  const reparte = accion.a === 'autorizado';
  const adelanto = Number(anticipo);
  const anticipoValido = !anticipo.trim() || (adelanto > 0 && adelanto < neto);

  async function confirmar() {
    setError(null);
    setTrabajando(true);
    try {
      await api.post(`/pagos/${pagoId}/transicion`, {
        hacia: accion.a,
        motivo: motivo.trim() || undefined,
        referencia: referencia.trim() || undefined,
        fechaPago: fechaPago ? new Date(fechaPago).toISOString() : undefined,
        anticipo: reparte && anticipo.trim() && adelanto > 0 ? adelanto : undefined,
      });
      onHecho();
    } catch (e) {
      setError(e);
      setTrabajando(false);
    }
  }

  if (pideMotivo && !pideReferencia) {
    return (
      <DialogoMotivo
        titulo={accion.etiqueta}
        textoBoton={accion.etiqueta}
        motivo={motivo}
        setMotivo={setMotivo}
        trabajando={trabajando}
        error={error}
        onConfirmar={confirmar}
        onCerrar={onCerrar}
        descripcion={<p>{accion.explicacion}</p>}
      />
    );
  }

  // Pagar sin autorización previa pide las dos cosas: la referencia
  // bancaria y por qué se pagó sin firma.
  const listo = (!pideReferencia || referencia.trim().length >= 3)
    && (!pideMotivo || motivo.trim().length >= 5)
    && anticipoValido;

  return (
    <Dialogo
      titulo={accion.etiqueta}
      onCerrar={onCerrar}
      pie={
        <>
          <button onClick={onCerrar} disabled={trabajando}>Cancelar</button>
          <button className="primario" onClick={confirmar} disabled={trabajando || !listo}>
            {trabajando ? 'Guardando…' : accion.etiqueta}
          </button>
        </>
      }
    >
      <Error error={error} />
      <p className="sub">{accion.explicacion}</p>

      {reparte && (
        <div className="campo">
          <label htmlFor="anticipo">Anticipo, si lo hay</label>
          <input
            id="anticipo" type="number" min="0" step="0.01" value={anticipo}
            placeholder="Vacío = se paga completo"
            onChange={(e) => setAnticipo(e.target.value)}
          />
          <span className="ayuda">
            Si pactaste que el taller cobre una parte por adelantado y el resto
            contra entrega, escríbelo aquí. Contabilidad transferirá exactamente
            esta cifra y después lo que falte: no puede partir el pago por su
            cuenta ni cambiar el importe.
          </span>

          {anticipo.trim() && !anticipoValido && (
            <div className="aviso error" style={{ marginTop: 6 }}>
              El anticipo tiene que ser mayor que cero y menor que {moneda(neto)}.
              Si se paga todo de una vez, deja el campo vacío.
            </div>
          )}

          {anticipo.trim() && anticipoValido && (
            <div className="aviso" style={{ marginTop: 6 }}>
              Se transferirán <strong>{moneda(adelanto)}</strong> ahora y
              {' '}<strong>{moneda(neto - adelanto)}</strong> al liquidar. El pago
              queda abierto hasta la liquidación, así que la orden no cierra
              todavía.
            </div>
          )}
        </div>
      )}

      {pideReferencia && (
        <>
          <div className="campo">
            <label htmlFor="referencia">Referencia bancaria *</label>
            <input
              id="referencia" className="mono" value={referencia} maxLength={80}
              placeholder="Folio del SPEI, cheque o transferencia"
              onChange={(e) => setReferencia(e.target.value)}
            />
          </div>

          <div className="campo">
            <label htmlFor="fechaPago">Fecha del pago</label>
            <input
              id="fechaPago" type="date" value={fechaPago}
              onChange={(e) => setFechaPago(e.target.value)}
            />
            <span className="ayuda">Si la dejas vacía, se registra hoy.</span>
          </div>
        </>
      )}

      {pideMotivo && (
        <div className="campo">
          <label htmlFor="motivoPago">Motivo *</label>
          <textarea
            id="motivoPago" rows={3} value={motivo} maxLength={500}
            placeholder="Por qué se paga sin autorización previa"
            onChange={(e) => setMotivo(e.target.value)}
          />
          <span className="ayuda">Queda en el historial del pago.</span>
        </div>
      )}
    </Dialogo>
  );
}

/**
 * Solicitar un pago a mano.
 *
 * Al técnico se le paga muchas veces antes de que la orden cierre: un
 * anticipo para que arranque, viáticos, la herramienta que tuvo que
 * comprar. Esos pagos existían igual y salían del sistema —caja chica,
 * una transferencia personal—, que es la forma más segura de que nadie
 * sepa cuánto se le ha dado a quién.
 *
 * Es independiente del pago del trabajo: cuando Operaciones valide el
 * costo al cerrar, nacerá el suyo completo. Conciliar los dos es de
 * quien paga, y por eso los dos viven en la misma bandeja.
 */
function SolicitarPago({
  onCerrar, onCreado,
}: {
  onCerrar: () => void;
  onCreado: () => void;
}) {
  const [beneficiario, setBeneficiario] =
    useState<'tecnico' | 'proveedor' | 'otro'>('tecnico');
  const [beneficiarioLibre, setBeneficiarioLibre] = useState('');
  const [tecnicoId, setTecnicoId] = useState('');
  const [proveedorId, setProveedorId] = useState('');
  const [ordenTrabajoId, setOrdenTrabajoId] = useState('');
  const [monto, setMonto] = useState('');
  const [retencion, setRetencion] = useState('');
  const [concepto, setConcepto] = useState('');
  const [tecnicos, setTecnicos] = useState<Opcion[]>([]);
  const [proveedores, setProveedores] = useState<Opcion[]>([]);
  const [busquedaOrden, setBusquedaOrden] = useState('');
  const [ordenes, setOrdenes] = useState<Orden[]>([]);
  const [error, setError] = useState<unknown>(null);
  const [trabajando, setTrabajando] = useState(false);

  useEffect(() => {
    api.get<Opcion[]>('/tecnicos/opciones').then(setTecnicos).catch(() => undefined);
    api.get<Pagina<Opcion>>('/proveedores', { estado: 'activo', limite: 200 })
      .then((r) => setProveedores(r.data))
      .catch(() => undefined);
  }, []);

  // La orden es opcional: hay pagos que no cuelgan de ninguna —una
  // herramienta, un adelanto de quincena— y obligar a inventarle una
  // ensucia el historial de esa orden.
  useEffect(() => {
    if (busquedaOrden.trim().length < 3) { setOrdenes([]); return undefined; }
    const t = setTimeout(() => {
      api.get<Pagina<Orden>>('/ordenes', { busqueda: busquedaOrden.trim(), limite: 10 })
        .then((r) => setOrdenes(r.data))
        .catch(() => setOrdenes([]));
    }, 300);
    return () => clearTimeout(t);
  }, [busquedaOrden]);

  const listo = concepto.trim().length >= 5
    && Number(monto) > 0
    && (beneficiario === 'tecnico' ? Boolean(tecnicoId)
      : beneficiario === 'proveedor' ? Boolean(proveedorId)
        : beneficiarioLibre.trim().length >= 3);

  async function guardar() {
    setError(null);
    setTrabajando(true);
    try {
      await api.post('/pagos', {
        beneficiario,
        tecnicoId: beneficiario === 'tecnico' ? tecnicoId : undefined,
        proveedorId: beneficiario === 'proveedor' ? proveedorId : undefined,
        beneficiarioLibre: beneficiario === 'otro'
          ? beneficiarioLibre.trim() : undefined,
        ordenTrabajoId: ordenTrabajoId || undefined,
        monto: Number(monto),
        retencion: retencion === '' ? undefined : Number(retencion),
        concepto: concepto.trim(),
      });
      onCreado();
    } catch (e) {
      setError(e);
      setTrabajando(false);
    }
  }

  const elegida = ordenes.find((o) => o.id === ordenTrabajoId);

  return (
    <Dialogo
      titulo="Solicitar un pago"
      onCerrar={onCerrar}
      pie={
        <>
          <button onClick={onCerrar} disabled={trabajando}>Cancelar</button>
          <button className="primario" onClick={guardar} disabled={trabajando || !listo}>
            {trabajando ? 'Guardando…' : 'Solicitar'}
          </button>
        </>
      }
    >
      <Error error={error} />

      <p className="sub">
        Queda pendiente de autorización, como cualquier otro pago. Pedirlo no
        es pagarlo.
      </p>

      <div className="rejilla-datos">
        <div className="campo">
          <label htmlFor="beneficiarioPago">A quién</label>
          <select
            id="beneficiarioPago" value={beneficiario}
            onChange={(e) => setBeneficiario(e.target.value as typeof beneficiario)}
          >
            <option value="tecnico">A un técnico</option>
            <option value="proveedor">A un proveedor</option>
            {/* No todo pago es a alguien del padrón: un permiso, una
                renta, una compra en mostrador, un reembolso. Sin esta
                salida se acababa dando de alta como proveedor a quien no
                vuelve, o pagando por fuera del sistema —que es peor:
                ese dinero no queda en ninguna parte—. */}
            <option value="otro">A alguien más</option>
          </select>
        </div>

        {beneficiario === 'otro' ? (
          <div className="campo">
            <label htmlFor="beneficiarioLibre">A quién *</label>
            <input
              id="beneficiarioLibre" value={beneficiarioLibre} maxLength={200}
              onChange={(e) => setBeneficiarioLibre(e.target.value)}
              placeholder="Nombre de quien recibe el pago"
            />
            <span className="ayuda">
              Como aparece en el comprobante. Un egreso sin destinatario no
              se puede auditar.
            </span>
          </div>
        ) : beneficiario === 'tecnico' ? (
          <div className="campo">
            <label htmlFor="tecnicoPago">Técnico *</label>
            <select
              id="tecnicoPago" value={tecnicoId}
              onChange={(e) => setTecnicoId(e.target.value)}
            >
              <option value="">— elige uno —</option>
              {tecnicos.map((t) => <option key={t.id} value={t.id}>{t.nombre}</option>)}
            </select>
          </div>
        ) : (
          <div className="campo">
            <label htmlFor="proveedorPago">Proveedor *</label>
            <select
              id="proveedorPago" value={proveedorId}
              onChange={(e) => setProveedorId(e.target.value)}
            >
              <option value="">— elige uno —</option>
              {proveedores.map((p) => <option key={p.id} value={p.id}>{p.nombre}</option>)}
            </select>
          </div>
        )}

        <div className="campo">
          <label htmlFor="montoPago">Monto *</label>
          <input
            id="montoPago" type="number" min="0" step="0.01" value={monto}
            onChange={(e) => setMonto(e.target.value)}
          />
        </div>

        <div className="campo">
          <label htmlFor="retencionPago">Retención</label>
          <input
            id="retencionPago" type="number" min="0" step="0.01" value={retencion}
            onChange={(e) => setRetencion(e.target.value)}
          />
          <span className="ayuda">Se descuenta del neto. Déjalo vacío si no aplica.</span>
        </div>

        <div className="campo ancho-total">
          <label htmlFor="conceptoPago">Concepto *</label>
          <input
            id="conceptoPago" value={concepto} maxLength={250}
            placeholder="Anticipo para arrancar, viáticos, herramienta…"
            onChange={(e) => setConcepto(e.target.value)}
          />
          <span className="ayuda">
            Sin esto hay que reconstruirlo de memoria tres meses después,
            cuando el técnico reclama que ya se le había dado algo.
          </span>
        </div>
      </div>

      <div className="campo">
        <label htmlFor="ordenPago">Orden de trabajo (opcional)</label>
        {ordenTrabajoId ? (
          <p>
            <span className="mono">{folioOT(elegida?.folio)}</span>{' '}
            <button className="enlace" onClick={() => { setOrdenTrabajoId(''); }}>
              quitar
            </button>
          </p>
        ) : (
          <>
            <input
              id="ordenPago" type="search" value={busquedaOrden}
              placeholder="Folio o sucursal…"
              onChange={(e) => setBusquedaOrden(e.target.value)}
            />
            <div style={{ display: 'grid', gap: 4, maxHeight: 140, overflow: 'auto' }}>
              {ordenes.map((o) => (
                <button
                  key={o.id} className="enlace" style={{ textAlign: 'left' }}
                  onClick={() => setOrdenTrabajoId(o.id)}
                >
                  + <span className="mono">{folioOT(o.folio)}</span>{' '}
                  {o.sucursal?.nombre ?? ''}
                </button>
              ))}
            </div>
          </>
        )}
        <span className="ayuda">
          Ligarlo a una orden ayuda a saber de qué trabajo salió. No hace
          falta: hay pagos que no cuelgan de ninguna.
        </span>
      </div>
    </Dialogo>
  );
}

/** Cómo se lee la prioridad de la compra en la ficha del pago. */
const ETIQUETAS_PRIORIDAD: Record<string, string> = {
  'urgente': 'Urgente',
  'alta': 'Alta',
  'media': 'Media',
  'baja': 'Baja',
};

function Dato({ etiqueta, valor, alClicar, detalle }: {
  etiqueta: string;
  valor: string | null;
  /** Si se pasa, el valor se pinta como enlace y lleva a algún lado. */
  alClicar?: () => void;
  /**
   * La línea de abajo, cuando el dato necesita explicarse.
   *
   * Un vencimiento sin decir desde cuándo se cuenta invita a suponer
   * que son treinta días desde hoy, que es justo lo que no es.
   */
  detalle?: string;
}) {
  return (
    <div className="campo">
      <label>{etiqueta}</label>
      {valor && alClicar
        ? <span><button className="enlace" onClick={alClicar}>{valor}</button></span>
        : <span>{valor ?? '—'}</span>}
      {detalle && <span className="ayuda">{detalle}</span>}
    </div>
  );
}

/**
 * Rehacer el plan de pago de un pago que ya existe.
 *
 * Karin: «gerencia al momento de autorizar y previo a que le llegue a
 * contabilidad, es quien decide si se paga parcial o total». Eso se
 * decide al autorizar, pero ahí se acababa el formulario: un pago
 * autorizado completo sólo se podía partir rechazándolo y volviéndolo a
 * autorizar, que deja dos firmas y un motivo de rechazo que no es verdad.
 *
 * Sigue siendo de quien autoriza —no de quien paga— y sólo mientras no
 * haya salido un peso: con un abono encima el plan ya está en marcha.
 */
function PlanDePago({
  pagoId, neto, anticipoActual, onCerrar, onHecho,
}: {
  pagoId: string;
  neto: number;
  anticipoActual: number | null;
  onCerrar: () => void;
  onHecho: () => void;
}) {
  const [anticipo, setAnticipo] = useState(
    anticipoActual ? String(anticipoActual) : '',
  );
  const [error, setError] = useState<unknown>(null);
  const [trabajando, setTrabajando] = useState(false);

  const adelanto = Number(anticipo);
  const valido = !anticipo.trim() || (adelanto > 0 && adelanto < neto);

  async function guardar() {
    setError(null);
    setTrabajando(true);
    try {
      await api.post(`/pagos/${pagoId}/plan`, {
        anticipo: anticipo.trim() && adelanto > 0 ? adelanto : null,
      });
      onHecho();
    } catch (e) {
      setError(e);
      setTrabajando(false);
    }
  }

  return (
    <Dialogo
      titulo="Plan de pago"
      onCerrar={onCerrar}
      pie={(
        <>
          <button onClick={onCerrar} disabled={trabajando}>Cancelar</button>
          <button className="primario" onClick={guardar} disabled={!valido || trabajando}>
            {trabajando ? 'Guardando…' : 'Guardar el plan'}
          </button>
        </>
      )}
    >
      <Error error={error} />

      <p className="ayuda">
        Se deben <strong>{moneda(neto)}</strong>. Si pactaste que el taller
        cobre una parte por adelantado y el resto contra entrega, escribe aquí
        el anticipo; déjalo vacío y el pago se ejecuta completo.
      </p>

      <div className="campo">
        <label htmlFor="plan-anticipo">Anticipo</label>
        <input
          id="plan-anticipo" type="number" min="0" step="0.01" value={anticipo}
          placeholder="Vacío = se paga completo"
          onChange={(e) => setAnticipo(e.target.value)}
        />
      </div>

      {anticipo.trim() && !valido && (
        <div className="aviso error">
          El anticipo tiene que ser mayor que cero y menor que {moneda(neto)}. Si
          se paga todo de una vez, deja el campo vacío.
        </div>
      )}

      {anticipo.trim() && valido && (
        <div className="aviso">
          Contabilidad transferirá <strong>{moneda(adelanto)}</strong> ahora y
          {' '}<strong>{moneda(neto - adelanto)}</strong> al liquidar, y no puede
          cambiar esas cifras.
        </div>
      )}

      <p className="ayuda" style={{ marginTop: 8 }}>
        Esto se puede rehacer mientras no haya salido dinero. En cuanto se
        registre el primer abono, el plan queda como está.
      </p>
    </Dialogo>
  );
}

/**
 * Registrar un abono contra un pago.
 *
 * Karin, de APRISA: «hay ocasiones que piden un anticipo antes de hacer
 * el trabajo y el resto contra entrega». Y después, corrigiendo quién lo
 * decide: «gerencia al momento de autorizar y previo a que le llegue a
 * contabilidad, es quien decide si se paga parcial o total, no es
 * contabilidad quien decide».
 *
 * Por eso el importe **no se captura**: llega calculado del plan que
 * firmó gerencia —el anticipo primero, lo que falte después— y aquí sólo
 * se confirma con su referencia bancaria. Mientras fue un campo editable,
 * quien transfería el dinero podía también decidir cuánto, que es justo
 * lo que separa la segregación de funciones.
 */
function RegistrarAbono({
  pagoId, falta, toca, esElAnticipo, onCerrar, onHecho,
}: {
  pagoId: string;
  falta: number;
  /** Lo que toca abonar ahora, según el plan autorizado. */
  toca: number;
  /** Si este movimiento es el anticipo que autorizó gerencia. */
  esElAnticipo: boolean;
  onCerrar: () => void;
  onHecho: () => void;
}) {
  const [referencia, setReferencia] = useState('');
  const [concepto, setConcepto] = useState('');
  const [fechaPago, setFechaPago] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [trabajando, setTrabajando] = useState(false);

  const cantidad = toca;
  const listo = cantidad > 0 && referencia.trim() !== '';

  async function guardar() {
    setError(null);
    setTrabajando(true);
    try {
      await api.post(`/pagos/${pagoId}/abonos`, {
        monto: cantidad,
        referencia: referencia.trim(),
        fechaPago: fechaPago || undefined,
        concepto: concepto.trim() || undefined,
      });
      onHecho();
    } catch (e) {
      setError(e);
      setTrabajando(false);
    }
  }

  return (
    <Dialogo
      titulo="Registrar un abono"
      onCerrar={onCerrar}
      pie={(
        <>
          <button onClick={onCerrar} disabled={trabajando}>Cancelar</button>
          <button className="primario" onClick={guardar} disabled={!listo || trabajando}>
            {trabajando ? 'Registrando…' : 'Registrar'}
          </button>
        </>
      )}
    >
      <Error error={error} />

      {esElAnticipo ? (
        <p className="ayuda">
          Gerencia autorizó pagar este compromiso <strong>en dos partes</strong>.
          Lo que toca ahora es el anticipo de <strong>{moneda(toca)}</strong>, y
          después quedarán {moneda(falta - toca)} por liquidar. El pago sigue
          abierto hasta la liquidación, así que la orden todavía no cierra.
        </p>
      ) : (
        <p className="ayuda">
          Quedan <strong>{moneda(falta)}</strong> por pagar, y eso es lo que se
          va a registrar. Si el taller pidió pagar en partes, eso lo autoriza
          gerencia antes de que el pago llegue aquí: no se puede decidir desde
          esta pantalla.
        </p>
      )}

      <div className="campo">
        <span className="etiqueta">Monto de este abono</span>
        {/* No es un campo: es la cifra que se va a transferir, tomada del
            plan que autorizó gerencia. Editable, volvería a ser una
            decisión de quien captura. */}
        <p style={{ fontSize: '1.3rem', fontWeight: 600, margin: '4px 0' }}>
          {moneda(toca)}
        </p>
      </div>

      <div className="campo">
        <label htmlFor="abono-referencia">Referencia bancaria</label>
        <input
          id="abono-referencia" value={referencia}
          onChange={(e) => setReferencia(e.target.value)}
        />
        <span className="ayuda">
          Sin ella no hay cómo encontrar esta transferencia en el estado de
          cuenta, que es justo cuando se necesita.
        </span>
      </div>

      <div className="campo">
        <label htmlFor="abono-fecha">Fecha del movimiento</label>
        <input
          id="abono-fecha" type="date" value={fechaPago}
          onChange={(e) => setFechaPago(e.target.value)}
        />
        <span className="ayuda">La del estado de cuenta. Vacío toma la de hoy.</span>
      </div>

      <div className="campo">
        <label htmlFor="abono-concepto">Por qué se abona en partes</label>
        <input
          id="abono-concepto" value={concepto}
          onChange={(e) => setConcepto(e.target.value)}
          placeholder="Anticipo antes del trabajo"
        />
      </div>
    </Dialogo>
  );
}
